import SideSheet from "components/sidesheet";
import { Container, NavTabs, Divider } from "ct-design";
import {
  FunctionComponent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import Cross from "icons/cross.svg";
import Rating from "./rating";
import Amenities from "./amenities";
import { ResultsContext } from "src/context/resultsContext/context";
import { getSeatChart } from "api/chart";
import { ChartContext } from "src/context/chartsContext/chartContext/context";
import Cancellation from "./cancellation";
import { Error } from "constants/common";
import useToast from "src/customHooks/useToast";
import { SelectedTabType } from "./types";
import { sendBusUIAction } from "src/analytics/useAnalytics";
import { cancellationPolicy } from "../chart/types";
import useGetChartData from "./customHooks/useGetChartData";
// import Divider from "components/divider";

interface SRPSideSheetProps {
  onClose: () => void;
  selectedTab: SelectedTabType;
  busIndex: number;
  solutionId: string;
  setSelectedTab: (val: SelectedTabType) => void;
}

const SRPSideSheet: FunctionComponent<SRPSideSheetProps> = ({
  onClose,
  selectedTab,
  busIndex,
  solutionId,
  setSelectedTab,
}) => {
  const [openSideSheet, setOpenSideSheet] = useState(true);
  const { filterResults, resultsData, showSeatLayout } =
    useContext(ResultsContext);
  const { setChartData, chartData } = useContext(ChartContext);
  const { handleHideSeatLayout } = useGetChartData();
  const [loadingSideSheet, setLoadingSideSheet] = useState(false);

  const { updateToast, toastComp } = useToast("SRP_SIDESHEET");
  const handleClose = () => {
    setOpenSideSheet(false);
    onClose();
  };

  const handleChartData = useCallback(async () => {
    // clearChartContext();
    setLoadingSideSheet(true);
    if (showSeatLayout) handleHideSeatLayout();
    try {
      const response = await getSeatChart(
        {
          solutionId: solutionId,

          searchId: resultsData.sc.searchId,
          itineraryId: null,
        },
        ""
      );
      if (response?.data?.success) {
        setChartData({
          [solutionId]: response.data.data,
        });
      } else {
        setOpenSideSheet(false);
        updateToast(true, Error.SOMETHING_WENT_WRONG, "warning");
        onClose();
      }
    } catch (e) {
      console.error(e);
      updateToast(true, Error.SOMETHING_WENT_WRONG, "warning");
    } finally {
      setLoadingSideSheet(false);
    }
  }, [
    handleHideSeatLayout,
    onClose,
    resultsData.sc.searchId,
    setChartData,
    showSeatLayout,
    solutionId,
    updateToast,
  ]);

  const renderSelectedTab = useMemo(() => {
    switch (selectedTab) {
      case "0":
        const amenities = filterResults?.buses[busIndex]?.meta?.amenities;
        sendBusUIAction(
          {
            actionName: "amenitiesClicked",
            fullAmenitiesShown: amenities?.length > 0 ? "yes" : "no",
            amenitiesCount: amenities?.length > 0 ? amenities.length : 0,
            searchId: chartData?.[solutionId]?.cc?.searchId,
          },
          "b_seat_click"
        );
        return <Amenities data={amenities} />;
      case "1":
        if (!chartData?.[solutionId]) handleChartData();
        const cancellationPolicy =
          chartData?.[solutionId]?.chart?.cancellationPolicy;
        sendBusUIAction(
          {
            actionName: "policiesClicked",
            cancellationPolicy:
              cancellationPolicy?.policy?.length > 0 ? "yes" : "no",
            searchId: chartData?.[solutionId]?.cc?.searchId,
          },
          "b_seat_click"
        );

        return (
          <Cancellation
            cfnrInfo={chartData?.[solutionId]?.fareInfo?.CFNR}
            data={cancellationPolicy as cancellationPolicy}
          />
        );

      case "2":
        if (!chartData?.[solutionId]) handleChartData();
        sendBusUIAction(
          {
            actionName: "ratings_clicked",
            searchId: chartData?.[solutionId]?.cc?.searchId,
          },
          "b_seat_click"
        );
        return <Rating data={chartData?.[solutionId]?.chart?.review} />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busIndex, chartData, filterResults.buses, selectedTab, solutionId]);

  return (
    <>
      {/* <OutsideClickHandler onOutsideClick={handleClose}> */}
      <SideSheet
        open={openSideSheet}
        direction="left"
        loading={loadingSideSheet}
        onClose={handleClose}
      >
        <Container>
          <Container className="py-4 px-6">
            <Cross onClick={handleClose} />
          </Container>
          <div className="w-100p">
            <NavTabs
              showBottomBorder={false}
              onClick={setSelectedTab}
              tabList={[
                { id: "0", label: "Amenities" },
                { id: "1", label: "Cancellation policy" },
                { id: "2", label: "Ratings" },
              ]}
              selectedTab={selectedTab.toString()}
              tabGap="16px"
              containerSpace="24px"
            />
            <Divider className="w-100p" />
          </div>
          {renderSelectedTab}
        </Container>
      </SideSheet>
      {/* </OutsideClickHandler> */}
      {toastComp}
    </>
  );
};

export default SRPSideSheet;
